import {
  familyDisplayName,
  familyPaxLabel,
  familyRoomTotalsPaise,
  shouldShowFamilyBreakdown,
  type FamilyBreakdown,
} from '@utazzo/shared';
import React from 'react';

const num = (paise: string): number => Number(paise);

// nowrap everywhere: amounts and names never break mid-value; the wrapper
// scrolls sideways only when the column really is too narrow.
const TH = 'whitespace-nowrap px-3 py-2 text-right text-xs font-medium text-zinc-500';
const TD = 'whitespace-nowrap px-3 py-2 text-right text-sm text-zinc-900 tabular-nums';
const SUB_TD = 'whitespace-nowrap px-3 py-1 text-right text-xs tabular-nums';

export const FamilyBreakdownTable = ({
  breakdown,
  isAdmin,
  canSeeTax,
  fmt,
}: {
  breakdown: FamilyBreakdown | null | undefined;
  isAdmin: boolean;
  canSeeTax: boolean;
  fmt: (paise: number) => string;
}): React.JSX.Element | null => {
  if (!breakdown || !shouldShowFamilyBreakdown(breakdown)) return null;

  // Columns before Total: Family, then Net/Markup/Discount/Upsell (admin), GST/TCS (tax visible).
  const leadCols = 1 + (isAdmin ? 4 : 0) + (canSeeTax ? 2 : 0);

  return (
    <section className="mt-4 rounded-lg border border-zinc-200 bg-white p-4">
      <h3 className="mb-2 text-sm font-semibold text-zinc-900">Price per family</h3>
      <div className="overflow-x-auto">
        <table className="w-full min-w-max border-collapse">
          <thead>
            <tr className="border-b border-zinc-200">
              <th className="px-3 py-2 text-left text-xs font-medium whitespace-nowrap text-zinc-500">
                Family
              </th>
              {isAdmin && <th className={TH}>Net</th>}
              {isAdmin && <th className={TH}>Markup</th>}
              {isAdmin && <th className={TH}>Discount</th>}
              {isAdmin && <th className={TH}>Upsell</th>}
              {canSeeTax && <th className={TH}>GST</th>}
              {canSeeTax && <th className={TH}>TCS</th>}
              <th className={TH}>Total</th>
            </tr>
          </thead>
          <tbody>
            {breakdown.families.map((family, i) => {
              const roomTotals = familyRoomTotalsPaise(family);
              return (
                <React.Fragment key={family.groupId}>
                  <tr className="border-b border-zinc-100">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-sm font-medium text-zinc-900">
                        {familyDisplayName(family, i)}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {familyPaxLabel(family.paxSummary)} · {family.rooms.length}{' '}
                        {family.rooms.length === 1 ? 'room' : 'rooms'}
                      </div>
                    </td>
                    {isAdmin && <td className={TD}>{fmt(num(family.netCostPaise))}</td>}
                    {isAdmin && <td className={TD}>{fmt(num(family.markupPaise))}</td>}
                    {isAdmin && <td className={TD}>{fmt(num(family.discountPaise))}</td>}
                    {isAdmin && <td className={TD}>{fmt(num(family.upsellPaise))}</td>}
                    {canSeeTax && <td className={TD}>{fmt(num(family.gstPaise))}</td>}
                    {canSeeTax && <td className={TD}>{fmt(num(family.tcsPaise))}</td>}
                    <td className={`${TD} font-semibold`}>{fmt(num(family.totalPaise))}</td>
                  </tr>
                  {family.rooms.map((room, ri) => (
                    <tr key={`${family.groupId}-${room.index}`} className="text-zinc-500">
                      <td className="px-3 py-1 pl-6 text-xs whitespace-nowrap">
                        Room {ri + 1} · {familyPaxLabel(room.paxSummary)}
                      </td>
                      {isAdmin && <td className={SUB_TD}>{fmt(num(room.netCostPaise))}</td>}
                      {isAdmin && <td colSpan={3} />}
                      {canSeeTax && <td colSpan={2} />}
                      <td className={SUB_TD}>{fmt(Number(roomTotals[ri] ?? 0n))}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-zinc-200">
              <td className="px-3 py-2 text-sm font-semibold text-zinc-900" colSpan={leadCols}>
                Total
              </td>
              <td className={`${TD} font-bold`}>{fmt(num(breakdown.grandTotalPaise))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};
